/** Upload dialog: the API needs a specific description per file, so picked or dropped files are described here before upload */

import { useId, useMemo, useState, type FormEvent } from 'react'
import { FileIcon } from 'lucide-react'
import { useT, type TranslationKey } from '../../i18n'
import { useAppStore } from '../../stores/app-store'
import { formatBytes } from '../../lib/format-bytes'
import { cn } from '../../lib/utils'
import { Modal } from '../ui/modal'
import { buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass, inputClass, labelClass } from '../ui/styles'
import { MAX_DESCRIPTION_CHARS, descriptionProblem, partitionBySize, type DescriptionProblem } from './storage-tree-utils'

export interface DescribedFile {
  file: File
  description: string
}

const PROBLEM_KEYS: Record<DescriptionProblem, TranslationKey> = {
  required: 'storage.upload.descriptionRequired',
  tooLong: 'storage.upload.descriptionTooLong',
  generic: 'storage.upload.descriptionGeneric',
}

interface StorageUploadDialogProps {
  files: File[]
  /** Display name of the destination folder */
  folderName: string
  onClose: () => void
  onSubmit: (items: DescribedFile[]) => void
}

export function StorageUploadDialog({ files, folderName, onClose, onSubmit }: StorageUploadDialogProps) {
  const t = useT()
  const id = useId()
  const locale = useAppStore((s) => s.locale)
  const { valid, oversized } = useMemo(() => partitionBySize(files), [files])
  const [descriptions, setDescriptions] = useState<string[]>(() => valid.map(() => ''))
  const [showErrors, setShowErrors] = useState(false)
  const problems = descriptions.map(descriptionProblem)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (problems.some(Boolean)) {
      setShowErrors(true)
      // Move focus to the first field that needs fixing
      const first = problems.findIndex(Boolean)
      document.getElementById(`${id}-desc-${first}`)?.focus()
      return
    }
    onSubmit(valid.map((file, i) => ({ file, description: descriptions[i].trim() })))
  }

  return (
    <Modal title={t('storage.upload.title')} onClose={onClose} widthClass="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <p className={hintClass}>{t('storage.upload.destination', { name: folderName })}</p>

        {oversized.length > 0 && (
          <div role="alert" className={errorTextClass}>
            <p>{t('storage.upload.tooLarge', { count: oversized.length })}</p>
            <ul className="mt-1 list-inside list-disc">
              {oversized.map((f) => (
                <li key={`${f.name}-${f.size}`} className="break-all">
                  {f.name} ({formatBytes(f.size, locale)})
                </li>
              ))}
            </ul>
          </div>
        )}

        {valid.length > 0 && (
          <>
            <p className={hintClass}>{t('storage.upload.descriptionHint')}</p>
            <ul className="max-h-[50vh] space-y-4 overflow-y-auto pr-1">
              {valid.map((file, i) => {
                const fieldId = `${id}-desc-${i}`
                const problem = showErrors ? problems[i] : null
                return (
                  <li key={`${file.name}-${file.size}-${i}`} className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm text-fg">
                      <FileIcon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                      <span className="shrink-0 text-xs text-fg-subtle">{formatBytes(file.size, locale)}</span>
                    </div>
                    <label htmlFor={fieldId} className={cn(labelClass, 'sr-only')}>
                      {t('storage.upload.descriptionFor', { name: file.name })}
                    </label>
                    <textarea
                      id={fieldId}
                      value={descriptions[i]}
                      onChange={(e) => setDescriptions((prev) => prev.map((d, j) => (j === i ? e.target.value : d)))}
                      rows={2}
                      maxLength={MAX_DESCRIPTION_CHARS}
                      placeholder={t('storage.upload.descriptionPlaceholder')}
                      aria-invalid={problem ? true : undefined}
                      aria-describedby={problem ? `${fieldId}-error` : undefined}
                      className={cn(inputClass, 'resize-y', problem && 'border-danger')}
                    />
                    {problem && (
                      <p id={`${fieldId}-error`} className="text-xs text-danger">
                        {t(PROBLEM_KEYS[problem], { max: MAX_DESCRIPTION_CHARS })}
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonSecondaryClass}>
            {valid.length > 0 ? t('common.cancel') : t('common.close')}
          </button>
          {valid.length > 0 && (
            <button type="submit" className={buttonPrimaryClass}>
              {t('storage.upload.submit', { count: valid.length })}
            </button>
          )}
        </div>
      </form>
    </Modal>
  )
}
